import React, { useEffect, useRef, useCallback, useState } from 'react';
import useChatStore from '../../stores/useChatStore';
import useUserStore from '../../stores/useUserStore';
import FlerpieMessageBubble from './FlerpieMessageBubble';
import useSSEStream from './useSSEStream';

const STARTER_QUESTIONS = [
  'Do we have enough drones for next month\'s events?',
  'What\'s our average flag turnaround time?',
  'Which drones have been repaired the most?',
  'Show me production trends over the last 6 months',
];

const FlerpieMessageThread = () => {
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const streamBuffer = useChatStore((s) => s.streamBuffer);
  const activeConversationId = useChatStore((s) => s.activeConversationId);
  const sharedOpen = useChatStore((s) => !!(s.activeThread && s.activeThread.shared));
  const { sendMessage } = useSSEStream();

  // Someone else may be typing in a shared thread: ask every eight seconds.
  useEffect(() => {
    if (!sharedOpen) return undefined;
    const id = setInterval(() => useChatStore.getState().refreshActiveThread(), 8000);
    return () => clearInterval(id);
  }, [sharedOpen, activeConversationId]);
  const userScrolledUp = useRef(false);
  const lastMessageCount = useRef(0);
  const observerRef = useRef(null);

  // Ref callback — attaches MutationObserver whenever the scroll div mounts
  const scrollRef = useCallback((el) => {
    // Clean up old observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!el) return;

    // Auto-scroll on any DOM change (typing effect, new messages)
    const observer = new MutationObserver(() => {
      if (!userScrolledUp.current) {
        el.scrollTop = el.scrollHeight;
      }
    });
    observer.observe(el, { childList: true, subtree: true, characterData: true });
    observerRef.current = observer;

    // Scroll to bottom immediately on mount
    el.scrollTop = el.scrollHeight;
  }, []);

  // Cleanup observer on unmount (the callback ref handles remounts, but not final unmount)
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, []);

  const handleScroll = useCallback((e) => {
    const el = e.currentTarget;
    userScrolledUp.current = el.scrollHeight - el.scrollTop - el.clientHeight > 150;
  }, []);

  // Reset scroll lock when new messages arrive
  useEffect(() => {
    if (messages.length !== lastMessageCount.current) {
      lastMessageCount.current = messages.length;
      userScrolledUp.current = false;
    }
  }, [messages]);

  const userProfile = useChatStore((s) => s.userProfile);
  const setRole = useChatStore((s) => s.setRole);
  const firstname = useUserStore((s) => s.user.firstname);
  const needsOnboarding = userProfile && !userProfile.role_at_firefly;
  const [roleInput, setRoleInput] = useState('');

  // Empty state
  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-y-auto">
        <h4
          className="text-base font-semibold mb-1"
          style={{ color: '#ffcc33' }}
        >
          {needsOnboarding ? `Hey ${firstname || 'there'}!` : 'Hey there!'}
        </h4>
        <p className="text-gray-500 text-xs text-center mb-6 max-w-[280px] leading-relaxed">
          {needsOnboarding
            ? "We haven't met before! What do you do at Firefly?"
            : 'Ask me anything about your fleet, events, production, or inventory. I can query data and show charts too!'}
        </p>

        {needsOnboarding ? (
          /* Role text input */
          <div className="w-full max-w-[300px]">
            <div
              className="flex items-end gap-2 rounded-xl px-3 py-2"
              style={{
                background: 'rgba(58,58,58,0.4)',
                border: '1px solid rgba(255,204,51,0.06)',
              }}
            >
              <input
                type="text"
                value={roleInput}
                onChange={(e) => setRoleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && roleInput.trim()) setRole(roleInput.trim());
                }}
                placeholder='e.g. "I manage battery procurement"'
                maxLength={100}
                className="flex-1 bg-transparent text-[13px] text-gray-200 placeholder:text-on-surface-variant
                           outline-none leading-relaxed"
                autoFocus
              />
              <button
                aria-label="Submit your role"
                onClick={() => roleInput.trim() && setRole(roleInput.trim())}
                disabled={!roleInput.trim()}
                className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                           transition-all duration-200 disabled:opacity-20"
                style={{
                  background: roleInput.trim() ? 'rgba(255,204,51,0.15)' : 'transparent',
                  color: roleInput.trim() ? '#ffcc33' : '#555',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 7L6 12L13 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
        ) : (
          /* Starter suggestions */
          <div className="w-full max-w-[300px] space-y-2">
            {STARTER_QUESTIONS.map((q) => (
              <button
                key={q}
                onClick={() => sendMessage(activeConversationId, q)}
                className="w-full text-left px-3.5 py-2.5 rounded-xl text-xs
                           transition-all duration-200 group"
                style={{
                  background: 'rgba(255,204,51,0.04)',
                  border: '1px solid rgba(255,204,51,0.08)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,204,51,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(255,204,51,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,204,51,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255,204,51,0.08)';
                }}
              >
                <span className="text-gray-400 group-hover:text-gray-200 transition-colors">
                  {q}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Disclaimer */}
        <p className="text-[10px] text-gray-600 text-center mt-6 px-4 leading-relaxed">
          Flerpie is still learning. Double-check the answers he gives you.
        </p>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="absolute inset-0 overflow-y-auto px-4 py-3 space-y-3"
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(255,204,51,0.15) transparent',
        overscrollBehavior: 'contain',
      }}
    >
      {messages.map((msg) => {
        // Streaming placeholder message
        if (msg.id === '__streaming') {
          return (
            <FlerpieMessageBubble
              key="streaming"
              message={{
                ...msg,
                content: streamBuffer,
              }}
              isStreaming
            />
          );
        }
        return <FlerpieMessageBubble key={msg.id} message={msg} />;
      })}

      {/* Disclaimer at bottom of conversation */}
      {messages.length > 0 && (
        <p className="text-[10px] text-gray-600 text-center pt-2 pb-1">
          Flerpie is still learning. Double-check the answers he gives you.
        </p>
      )}
    </div>
  );
};

export default FlerpieMessageThread;
